import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Modal } from "antd";
import { getSupabaseClient } from "../../core/supabase/client.js";
import { appFeedback } from "@/shared/feedback/appFeedback";

type Review = { id: string; text: string; page: string };

const MARQUEE_CSS = `
@keyframes amax-mq {
  from { transform: translateX(0); }
  to   { transform: translateX(calc(-100% - var(--amax-mq-gap))); }
}
.amax-mq-track { animation: amax-mq var(--amax-mq-dur) linear infinite; }
.amax-mq-track-rev { animation: amax-mq var(--amax-mq-dur) linear infinite reverse; }
.amax-mq-root:hover .amax-mq-track,
.amax-mq-root:hover .amax-mq-track-rev { animation-play-state: paused; }
`;

async function fetchReviews(): Promise<Review[]> {
  const { data, error } = await getSupabaseClient()
    .from("activity_logs")
    .select("id, metadata, created_at")
    .eq("type", "user_review")
    .order("created_at", { ascending: false })
    .limit(60);

  if (error || !data) return [];
  const rows = data as Array<{ id: string; metadata: Record<string, string> | null }>;
  return rows
    .map((row) => ({
      id: row.id,
      text: (row.metadata ?? {}).text ?? "",
      page: (row.metadata ?? {}).page ?? "",
    }))
    .filter((r) => r.text.trim());
}

async function postReview(text: string): Promise<void> {
  const { error } = await getSupabaseClient().from("activity_logs").insert({
    type: "user_review",
    status: "completed",
    metadata: { text, page: window.location.pathname },
  } as never);
  if (error) throw error;
}

function MarqueeRow({
  items,
  reverse,
  duration,
}: {
  items: Review[];
  reverse?: boolean;
  duration: number;
}) {
  const trackClass = reverse ? "amax-mq-track-rev" : "amax-mq-track";
  return (
    <div
      className="amax-mq-root"
      style={{
        display: "flex",
        overflow: "hidden",
        gap: "var(--amax-mq-gap)",
        ["--amax-mq-gap" as string]: "1rem",
        ["--amax-mq-dur" as string]: `${duration}s`,
        maskImage: "linear-gradient(90deg,transparent,black 6%,black 94%,transparent)",
        WebkitMaskImage: "linear-gradient(90deg,transparent,black 6%,black 94%,transparent)",
      }}
    >
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className={trackClass}
          style={{
            display: "flex",
            flexShrink: 0,
            justifyContent: "space-around",
            gap: "var(--amax-mq-gap)",
          }}
        >
          {items.map((r) => (
            <span
              key={r.id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                whiteSpace: "nowrap",
                fontSize: 12,
                color: "var(--text3)",
                opacity: 0.72,
                userSelect: "none",
              }}
            >
              <span style={{ color: "var(--accent)", opacity: 0.55, fontSize: 9 }}>★</span>
              {r.text}
              {r.page && r.page !== "/" && (
                <span style={{ opacity: 0.45, fontSize: 10 }}>[{r.page}]</span>
              )}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

export function ReviewMarquee() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    fetchReviews().then(setReviews);
  }, []);

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await postReview(trimmed);
      setReviews((prev) => [
        { id: `local-${Date.now()}`, text: trimmed, page: window.location.pathname },
        ...prev,
      ]);
      appFeedback.message('success', "Review ghi nhận ✓");
      setText("");
      setOpen(false);
    } catch {
      appFeedback.error("Gửi thất bại — thử lại sau");
    } finally {
      setSubmitting(false);
    }
  };

  const mid = Math.ceil(reviews.length / 2);
  const row1 = reviews.slice(0, mid);
  const row2 = reviews.slice(mid);

  return (
    <div
      style={{
        position: "relative",
        zIndex: 1,
        flexShrink: 0,
        width: "100%",
        borderTop: "1px solid var(--border)",
        background: "var(--bg)",
        paddingTop: 8,
        paddingBottom: 10,
        display: "flex",
        flexDirection: "column",
        gap: 5,
      }}
    >
      <style>{MARQUEE_CSS}</style>

      {row1.length > 0 && <MarqueeRow items={row1} duration={40} />}
      {row2.length > 0 && <MarqueeRow items={row2} reverse duration={55} />}

      <div style={{ textAlign: "center", marginTop: 4 }}>
        <Button
          size="small"
          type="dashed"
          onClick={() => setOpen(true)}
          style={{ fontSize: 11, color: "var(--text3)", borderColor: "var(--border)", height: 22 }}
        >
          + viết review / ghi bug
        </Button>
      </div>

      <Modal
        title="Viết review / ghi bug"
        open={open}
        onCancel={() => { setOpen(false); setText(""); }}
        onOk={handleSubmit}
        okText="Gửi"
        cancelText="Hủy"
        confirmLoading={submitting}
        width={440}
      >
        <Input.TextArea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Mô tả bug, cảm nghĩ, hoặc lời tâm sự về tình trạng hiện tại…"
          rows={4}
          maxLength={300}
          showCount
          autoFocus
          onPressEnter={(e) => { if (e.ctrlKey || e.metaKey) handleSubmit(); }}
        />
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--text3)", opacity: 0.7 }}>
          Trang: <code style={{ fontSize: 11 }}>{window.location.pathname}</code>
          &nbsp;·&nbsp;Ctrl+Enter để gửi
        </div>
      </Modal>
    </div>
  );
}
